import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  getRuntimeBundleName,
  writeDiagnosticsWindow,
} from '../components/diagnostics/diagnosticsUtils';

/**
 * Custom hook to manage diagnostics mode, debug mode, diagnostic data rows,
 * and popout diagnostics window lifecycle.
 *
 * @param {Object} params
 * @param {Object} params.selectedChain - Current chain config object
 * @param {string} params.selectedChainKey - Current chain key
 * @param {string} params.networkDisplayName - Formatted network display name
 * @param {string|number} params.currentChainId - Currently connected chain ID
 * @param {boolean} params.isWalletNetworkAligned - Whether wallet network matches selected chain
 * @param {boolean} params.isCorrectNetwork - Raw network correctness flag
 * @param {boolean} params.isNetworkSwitchModalOpen - Whether network switch modal is open
 * @param {boolean} params.isConnected - Wallet connection flag
 * @param {string} params.address - Connected wallet address
 * @param {string} params.selectedStablecoin - Selected stablecoin symbol
 * @param {Object} params.selectedCoin - Selected coin configuration object
 * @param {Object} params.solanaBalanceTargets - Input ATA and USDN ATA addresses
 * @param {Object} params.solanaWallet - Solana wallet instance
 * @param {string} params.walletProviderLabel - Label for EVM wallet provider
 * @param {Object} params.balanceDiagnostics - Diagnostic metadata from balance fetch
 * @param {string} params.stablecoinBalance - Stablecoin balance string
 * @param {boolean} params.stablecoinLoading - Stablecoin balance loading flag
 * @param {string} params.usdnBalance - USDN balance string
 * @param {boolean} params.usdnLoading - USDN balance loading flag
 * @param {Function} [params.onPopoutBlocked] - Optional callback if popout window is blocked
 */
export const useDiagnostics = ({
  selectedChain,
  selectedChainKey,
  networkDisplayName,
  currentChainId,
  isWalletNetworkAligned,
  isCorrectNetwork,
  isNetworkSwitchModalOpen,
  isConnected,
  address,
  selectedStablecoin,
  selectedCoin,
  solanaBalanceTargets = { inputAta: '', usdnAta: '' },
  solanaWallet,
  walletProviderLabel,
  balanceDiagnostics,
  stablecoinBalance,
  stablecoinLoading,
  usdnBalance,
  usdnLoading,
  onPopoutBlocked,
}) => {
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [isDiagnosticsMode, setIsDiagnosticsMode] = useState(false);
  const diagnosticsWindowRef = useRef(null);

  useEffect(() => {
    const checkDebugParam = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const debugParam = urlParams.get('debug');
      const diagnosticsParam = urlParams.get('diagnostics');
      setIsDebugMode(debugParam === 'true');
      setIsDiagnosticsMode(diagnosticsParam === 'true');
    };
    checkDebugParam();
    window.addEventListener('popstate', checkDebugParam);
    return () => window.removeEventListener('popstate', checkDebugParam);
  }, []);

  const diagnosticsRows = useMemo(
    () => [
      ['Bundle', getRuntimeBundleName() || 'unknown'],
      ['Chain', `${selectedChain?.name || selectedChainKey} (${selectedChainKey})`],
      ['Network', `${networkDisplayName} / ${currentChainId || 'n/a'}`],
      ['Wallet network aligned', String(isWalletNetworkAligned)],
      ['Wallet network correct flag', String(isCorrectNetwork)],
      ['Target network id', selectedChain?.networkId || 'n/a'],
      ['Network switch modal', String(isNetworkSwitchModalOpen)],
      ['RPC URL', selectedChain?.rpcUrl || 'n/a'],
      [
        'Wallet adapter',
        selectedChain?.adapter === 'solana'
          ? solanaWallet?.wallet?.adapter?.name || 'none'
          : walletProviderLabel || 'EVM Wallet',
      ],
      ['Wallet connected', String(isConnected)],
      ['Wallet address', address || 'none'],
      ['Selected token', selectedStablecoin],
      ['Input target', selectedCoin?.address || selectedCoin?.mint || 'none'],
      ['Input ATA', solanaBalanceTargets.inputAta || 'n/a'],
      ['Input token program', selectedCoin?.tokenProgramId || 'n/a'],
      ['USDN target', selectedChain?.usdnAddress || selectedChain?.usdnMint || 'none'],
      ['USDN ATA', solanaBalanceTargets.usdnAta || 'n/a'],
      ['USDN token program', selectedChain?.usdnTokenProgramId || 'n/a'],
      ['Balance request', balanceDiagnostics?.status || 'none'],
      ['Balance reason/error', balanceDiagnostics?.reason || balanceDiagnostics?.error || 'n/a'],
      ['Raw balance error', balanceDiagnostics?.rawError || 'n/a'],
      [
        'Balance read account',
        balanceDiagnostics?.adapterDiagnostics?.account || balanceDiagnostics?.account || 'n/a',
      ],
      [
        'Input read attempts',
        JSON.stringify(balanceDiagnostics?.adapterDiagnostics?.inputAttempts || []),
      ],
      [
        'USDN read attempts',
        JSON.stringify(balanceDiagnostics?.adapterDiagnostics?.outputAttempts || []),
      ],
      ['Input balance', stablecoinLoading ? 'loading' : stablecoinBalance],
      ['USDN balance', usdnLoading ? 'loading' : usdnBalance],
    ],
    [
      address,
      balanceDiagnostics,
      currentChainId,
      isCorrectNetwork,
      isConnected,
      isNetworkSwitchModalOpen,
      isWalletNetworkAligned,
      networkDisplayName,
      usdnBalance,
      usdnLoading,
      selectedChain,
      selectedChainKey,
      selectedCoin,
      selectedStablecoin,
      solanaBalanceTargets,
      solanaWallet,
      stablecoinBalance,
      stablecoinLoading,
      walletProviderLabel,
    ]
  );

  const handleOpenDiagnosticsWindow = useCallback(() => {
    const diagnosticsWindow = window.open('', 'usdn-diagnostics', 'width=520,height=720');
    if (!diagnosticsWindow) {
      if (onPopoutBlocked) {
        onPopoutBlocked('Diagnostics pop-out was blocked by the browser.');
      }
      return;
    }
    diagnosticsWindowRef.current = diagnosticsWindow;
    writeDiagnosticsWindow(diagnosticsWindow, diagnosticsRows);
    diagnosticsWindow.focus();
  }, [diagnosticsRows, onPopoutBlocked]);

  useEffect(() => {
    if (!diagnosticsWindowRef.current || diagnosticsWindowRef.current.closed) return;
    writeDiagnosticsWindow(diagnosticsWindowRef.current, diagnosticsRows);
  }, [diagnosticsRows]);

  return {
    isDebugMode,
    isDiagnosticsMode,
    diagnosticsRows,
    handleOpenDiagnosticsWindow,
  };
};
